import React, { useState, useEffect, useRef, useCallback, useMemo, Fragment } from "react";
import { Building2, Pencil, Search, Trash2 } from "lucide-react";
import { dashboardToast } from '../../utils/dashboardToast';
import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import ContentLoader from "../../components/ContentLoader";
import SaListPageShell from "../../components/sa/SaListPageShell";
import RecordDetailModal from "../../components/RecordDetailModal";
import { getCompanyList, addOrUpdateCompany, deleteCompany } from "../../services/companyApi";
import { getIndustries } from "../../services/CostEstimationsetupApi";
import TableSortSelect from "../../components/TableSortSelect";
import { Alert, Button, Input, Modal, Pagination, PhoneInput, Select, Textarea } from "../../components/ui";
import { TABLE_SORT } from "../../utils/tableSort";
import "../../assets/css/dashboard.css";

const EMPTY_COMPANY_FORM = {
  companyId: 0,
  companyName: "",
  companyInfo: "",
  companyContactInfo: "",
  industry: "",
  companySize: "",
  engagementGoal: "",
  additionalContext: "",
};

/** Display-only masks (`.` = digit). Longer dial codes are matched first. */
const PHONE_DISPLAY_FORMATS: { dial: string; format: string }[] = [
  { dial: "971", format: ".. ..." },
  { dial: "966", format: ".. ..." },
  { dial: "880", format: "...." },
  { dial: "234", format: "... ..." },
  { dial: "92", format: "... ......." },
  { dial: "91", format: "..... ....." },
  { dial: "86", format: "... .... ...." },
  { dial: "81", format: ".. .... ...." },
  { dial: "61", format: "... ..." },
  { dial: "49", format: ".... ......." },
  { dial: "44", format: ".... ......" },
  { dial: "33", format: ". .. .. .. .." },
  { dial: "39", format: "... ......" },
  { dial: "34", format: "... ..." },
  { dial: "31", format: ".. ..." },
  { dial: "7", format: "... ...-.." },
  { dial: "1", format: "(...) ...-...." },
].sort((a, b) => b.dial.length - a.dial.length);

function applyPhoneMask(national: string, format: string): string {
  let i = 0;
  let out = "";
  for (const ch of format) {
    if (i >= national.length) break;
    if (ch === ".") {
      out += national[i++];
    } else {
      out += ch;
    }
  }
  if (i < national.length) out += national.slice(i);
  return out;
}

/** Formats a stored contact number for list display only (e.g. +1 (212) 555-0199). */
function formatPhoneForDisplay(raw?: string | null): string {
  if (raw == null) return "—";
  const original = String(raw).trim();
  if (!original) return "—";

  const digits = original.replace(/\D/g, "");
  if (!digits) return original;

  // Bare 10-digit NANP numbers (no country code stored)
  if (digits.length === 10) {
    return `+1 ${applyPhoneMask(digits, "(...) ...-....")}`;
  }

  for (const { dial, format } of PHONE_DISPLAY_FORMATS) {
    if (!digits.startsWith(dial) || digits.length <= dial.length) continue;
    const national = digits.slice(dial.length);
    return `+${dial} ${applyPhoneMask(national, format)}`;
  }

  return `+${digits}`;
}

/** Muted dash for missing table values */
const EmptyCell = () => <span className="app-td__empty">—</span>;

/** Secondary text cell; falls back to empty dash */
const MutedCell = ({ value, className = "app-td__desc" }) => {
  const text = value == null ? "" : String(value).trim();
  if (!text || text === "—") return <EmptyCell />;
  return (
    <span className={className} title={text}>
      {text}
    </span>
  );
};

const COMPANY_SIZE_OPTIONS = [
  {
    value: "1–10 employees",
    label: "1–10 employees",
  },
  {
    value: "11–50 employees",
    label: "11–50 employees",
  },
  {
    value: "51–250 employees",
    label: "51–250 employees",
  },
  {
    value: "251–1,000 employees",
    label: "251–1,000 employees",
  },
  {
    value: "1,000–5,000 employees",
    label: "1,000–5,000 employees",
  },
  {
    value: "5,000+ employees",
    label: "5,000+ employees",
  },
];

const CompanyManagement = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState(EMPTY_COMPANY_FORM);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewCompany, setViewCompany] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [industriesLoading, setIndustriesLoading] = useState(false);
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const [searchTerm, setSearchTerm] = useState("");
  const dataFetchedRef = useRef(false);

  const [apiPageInfo, setApiPageInfo] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
  });

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const fetchCompanies = useCallback(async (page = 1, pageSize = 10, sort = sortKey, search = searchTerm) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getCompanyList(page, pageSize, sort, search);
      setCompanies(result.items || []);
      setApiPageInfo({
        currentPage: result.pageNumber || page,
        pageSize: result.pageSize || pageSize,
        totalPages: Math.max(
          1,
          Math.ceil((result.totalCount || 0) / (result.pageSize || pageSize))
        ),
        totalCount: result.totalCount || 0,
      });
    } catch (err) {
      setError("Failed to fetch companies. Please try again.");
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [sortKey, searchTerm]);

  const fetchIndustries = useCallback(async () => {
    setIndustriesLoading(true);
    try {
      const response = await getIndustries({
        isActive: true,
        page: 1,
        pageSize: 1000,
      });
      setIndustries(response?.data?.data || []);
    } catch (err) {
      console.error("Error fetching industries:", err);
      setIndustries([]);
    } finally {
      setIndustriesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies(apiPageInfo.currentPage, apiPageInfo.pageSize);
    fetchIndustries();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!dataFetchedRef.current) {
      dataFetchedRef.current = true;
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      fetchCompanies(1, apiPageInfo.pageSize, sortKey, searchTerm);
    }, 300);

    return () => window.clearTimeout(timerId);
    // eslint-disable-next-line
  }, [searchTerm]);

  const goToPage = (page) => {
    const newPage = Math.min(Math.max(page, 1), apiPageInfo.totalPages);
    if (newPage !== apiPageInfo.currentPage) {
      fetchCompanies(newPage, apiPageInfo.pageSize);
    }
  };

  const handleSortChange = (nextSort) => {
    setSortKey(nextSort);
    fetchCompanies(1, apiPageInfo.pageSize, nextSort, searchTerm);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleShow = useCallback((company = null) => {
    console.log("handleShow called with company:", company);
    
    if (company) {
      // Debug: Log all possible ID properties
      console.log("company.companyId:", company.companyId);
      console.log("company.id:", company.id);
      console.log("company.CompanyId:", company.CompanyId);
      console.log("company.ID:", company.ID);
      
      // Try different possible ID property names
      const companyId = company.companyId || company.id || company.CompanyId || company.ID || 0;
      
      console.log("Selected companyId:", companyId);
      
      const companyContactInfo = String(
        company.companyContactInfo || company.companyContact || ""
      ).replace(/\D/g, "");

      const newFormData = {
        companyId: companyId,
        companyName: company.companyName || "",
        companyInfo: company.companyInfo || "",
        companyContactInfo,
        industry: company.industry || company.Industry || "",
        companySize: company.companySize || company.CompanySize || company.company_size || "",
        engagementGoal: "",
        additionalContext: "",
      };
      
      console.log("Setting form data:", newFormData);
      setFormData(newFormData);
    } else {
      // Only set companyId to 0 for new companies
      setFormData({ ...EMPTY_COMPANY_FORM });
    }
    setFormSubmitted(false);
    setShowModal(true);
  }, []);

  const industryOptions = useMemo(() => {
    const options = industries.map((industry) => ({
      value: industry.name || "",
      label: industry.name,
    }));
    if (formData.industry && !industries.some((ind) => ind.name === formData.industry)) {
      options.push({ value: formData.industry, label: formData.industry });
    }
    return options;
  }, [industries, formData.industry]);

  const companySizeOptions = useMemo(() => {
    const options = COMPANY_SIZE_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
    }));
    if (formData.companySize && !COMPANY_SIZE_OPTIONS.some((o) => o.value === formData.companySize)) {
      options.push({ value: formData.companySize, label: formData.companySize });
    }
    return options;
  }, [formData.companySize]);

  const handleClose = useCallback(() => setShowModal(false), []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handlePhoneChange = useCallback((value) => {
    setFormData((prev) => ({ ...prev, companyContactInfo: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setFormSubmitted(true);
      if (!formData.companyName || !formData.companyContactInfo) return;

      const companyContactInfo = `+${formData.companyContactInfo}`;

      try {
        setLoading(true);

        const payload = {
          companyId: formData.companyId,
          companyName: formData.companyName,
          companyInfo: formData.companyInfo?.trim() || "",
          companyContactInfo,
          industry: formData.industry,
          companySize: formData.companySize,
          engagementGoal: "",
          additionalContext: "",
        };

        console.log("Sending payload:", payload);
        console.log("CompanyId being sent:", payload.companyId);
        console.log("Is editing?", payload.companyId > 0);

        await addOrUpdateCompany(payload);
        handleClose();
        dashboardToast.success(payload.companyId > 0 ? "Company updated successfully" : "Company created successfully");
        fetchCompanies(apiPageInfo.currentPage, apiPageInfo.pageSize);
      } catch (err) {
        console.error("Error saving company:", err);
        dashboardToast.error("Failed to save company. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [formData, handleClose, fetchCompanies, apiPageInfo.currentPage, apiPageInfo.pageSize]
  );

  const handleDelete = useCallback(
    (companyId) => {
      console.log("Delete button clicked with ID:", companyId);
      
      // Find company using multiple possible ID property names
      const company = companies.find((c) => 
        c.companyId === companyId || 
        c.id === companyId || 
        c.CompanyId === companyId || 
        c.ID === companyId
      );
      
      console.log("Found company for deletion:", company);
      
      if (company) {
        setCompanyToDelete(company);
        setShowDeleteModal(true);
      } else {
        console.error("Company not found with ID:", companyId);
        dashboardToast.error("Company not found for deletion");
      }
    },
    [companies]
  );
  
  // Fixed confirmDelete function with better error handling
  const confirmDelete = useCallback(async () => {
    if (!companyToDelete) {
      console.error("No company selected for deletion");
      return;
    }
  
    try {
      setLoading(true);
      
      // Get the correct ID from the company object
      const companyId = companyToDelete.companyId || 
                       companyToDelete.id || 
                       companyToDelete.CompanyId || 
                       companyToDelete.ID;
      
      console.log("Attempting to delete company with ID:", companyId);
      
      if (!companyId) {
        throw new Error("Invalid company ID");
      }
      
      await deleteCompany(companyId);
      dashboardToast.success("Company deleted successfully");
      
      // Refresh the companies list
      fetchCompanies(apiPageInfo.currentPage, apiPageInfo.pageSize);
      
    } catch (err) {
      console.error("Error deleting company:", err);
      const message =
        err instanceof Error ? err.message : "Failed to delete company. Please try again.";
      dashboardToast.error(message);
    } finally {
      setShowDeleteModal(false);
      setCompanyToDelete(null);
      setLoading(false);
    }
  }, [companyToDelete, fetchCompanies, apiPageInfo.currentPage, apiPageInfo.pageSize]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setCompanyToDelete(null);
  }, []);

  const openViewModal = useCallback((company) => {
    setViewCompany(company);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewCompany(null);
  }, []);

  return (
    <DashboardShell isMenuCollapsed={isMenuCollapsed}
      sidebar={<Menu onSelect={handleMenuSelection} selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />}>
      <SaListPageShell topBarTitle="Companies">
            <div className="dash-frame-parent1">
              <div className="dash-list-toolbar">
                <div className="dash-list-toolbar__intro">
                  <h5 className="dash-list-toolbar__title">Manage Companies</h5>
                  <p className="dash-list-toolbar__desc">
                    Create and manage companies, industry context, and contact information.
                  </p>
                </div>
                <div className="dash-list-toolbar__actions">
                  <div className="dash-search-field dash-table-search">
                    <Search className="dash-search-field__icon" aria-hidden />
                    <input
                      type="text"
                      placeholder="Search by name…"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="dash-search-field__input"
                      disabled={loading}
                    />
                  </div>
                  <TableSortSelect variant="chip"
                    value={sortKey}
                    onChange={handleSortChange}
                    disabled={loading}
                  />
                  <Button variant="primary" className="sa-create-btn" onClick={() => handleShow()}>
                    Create Company
                  </Button>
                </div>
              </div>

              {loading ? (
                <ContentLoader label="Loading companies…" />
              ) : error ? (
                <Alert variant="error">{error}</Alert>
              ) : (
                <Fragment>
                  <div className="app-table-wrap app-table-wrap--refined">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Company Name</th>
                          <th>Industry</th>
                          <th>Company Size</th>
                          <th>Description</th>
                          <th>Contact No.</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companies.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center">
                              <span className="app-td__desc">
                                No companies found. Create your first company.
                              </span>
                            </td>
                          </tr>
                        ) : (
                          companies.map((company) => {
                            const industry = company.industry || company.Industry || "";
                            const companySize =
                              company.companySize ||
                              company.CompanySize ||
                              company.company_size ||
                              "";
                            const contactDisplay = formatPhoneForDisplay(
                              company.companyContactInfo || company.companyContact
                            );
                            const companyName = company.companyName || "";

                            return (
                              <tr
                                key={company.companyId || company.id}
                                className="rdm-row-clickable"
                                onClick={() => openViewModal(company)}
                              >
                                <td>
                                  <span className="app-td__title">{companyName}</span>
                                </td>
                                <td>
                                  <MutedCell value={industry} />
                                </td>
                                <td>
                                  <MutedCell value={companySize} />
                                </td>
                                <td>
                                  <MutedCell value={company.companyInfo} />
                                </td>
                                <td>
                                  {contactDisplay === "—" ? (
                                    <EmptyCell />
                                  ) : (
                                    <span className="app-td__meta">{contactDisplay}</span>
                                  )}
                                </td>
                                <td
                                  className="dash-mobile-table-actions"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="btn-list-actions">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="btn-list-edit"
                                      title="Edit"
                                      aria-label={`Edit ${companyName}`}
                                      onClick={() => handleShow(company)}
                                    >
                                      <Pencil />
                                    </Button>
                                    <Button
                                      variant="danger"
                                      size="sm"
                                      className="btn-list-delete"
                                      title="Delete"
                                      aria-label={`Delete ${companyName}`}
                                      onClick={() =>
                                        handleDelete(company.companyId || company.id)
                                      }
                                    >
                                      <Trash2 />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  <Pagination
                    page={apiPageInfo.currentPage}
                    totalPages={apiPageInfo.totalPages}
                    onPageChange={goToPage}
                    className="justify-center mt-4"
                  />
                </Fragment>
              )}
            </div>
      </SaListPageShell>

      <Modal
        open={showModal}
        onClose={handleClose}
        size="lg"
        className="pm-project-form-modal"
        title={
          <span className="inline-flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {formData.companyId ? "Edit Company" : "Create New Company"}
          </span>
        }
        footer={
          <>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" form="company-form" variant="primary">
              {formData.companyId ? "Update Company" : "Create Company"}
            </Button>
          </>
        }
      >
        <form id="company-form" onSubmit={handleSubmit} className="pm-project-form" noValidate>
          <div className="pm-project-form__block">
            <div className="pm-project-form__section-label">Company Details</div>
            <div className="pm-project-form__grid">
              <Input
                label="Company Name"
                name="companyName"
                value={formData.companyName}
                onChange={handleChange}
                placeholder="Enter company name"
                required
                error={formSubmitted && !formData.companyName ? "Company name is required" : undefined}
              />
              <div className="app-field">
                <label className="app-field__label">
                  Contact No.
                  <span className="text-red-500 ml-0.5">*</span>
                </label>
                <PhoneInput
                  value={formData.companyContactInfo}
                  onChange={handlePhoneChange}
                  placeholder="Enter phone number"
                  error={
                    formSubmitted && !formData.companyContactInfo
                      ? "Contact number is required"
                      : undefined
                  }
                />
              </div>
              <Select
                label="Industry"
                name="industry"
                value={formData.industry}
                onChange={(value) => setFormData((prev) => ({ ...prev, industry: value }))}
                options={industryOptions}
                placeholder={industriesLoading ? "Loading industries..." : "Select Industry"}
                disabled={industriesLoading}
              />
              <Select
                label="Company Size"
                name="companySize"
                value={formData.companySize}
                onChange={(value) => setFormData((prev) => ({ ...prev, companySize: value }))}
                options={companySizeOptions}
                placeholder="Select Company Size"
              />
              <div style={{ gridColumn: "1 / -1" }}>
                <Textarea
                  label="Description"
                  name="companyInfo"
                  value={formData.companyInfo}
                  onChange={handleChange}
                  placeholder="Enter company description"
                  rows={3}
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        open={showDeleteModal}
        onClose={cancelDelete}
        title="Confirm Delete"
        footer={
          <>
            <Button variant="outline" onClick={cancelDelete}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p className="m-0">
          Are you sure you want to delete the company &quot;
          {companyToDelete?.companyName}&quot;?
        </p>
        <p className="mt-3 mb-0 app-field__error">
          This company must have no projects. Client and SuperUser accounts associated with
          this company will be deleted. SME, Admin, and SuperAdmin accounts are kept.
        </p>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={viewCompany?.companyName || "Company Details"}
        subtitle="Company record"
        fields={[
          { label: "Company Name", value: viewCompany?.companyName },
          {
            label: "Industry",
            value: viewCompany?.industry || viewCompany?.Industry,
          },
          {
            label: "Company Size",
            value:
              viewCompany?.companySize ||
              viewCompany?.CompanySize ||
              viewCompany?.company_size,
          },
          {
            label: "Contact No.",
            value: formatPhoneForDisplay(
              viewCompany?.companyContactInfo || viewCompany?.companyContact
            ),
          },
          {
            label: "Description",
            value: viewCompany?.companyInfo,
            fullWidth: true,
          },
        ]}
        actions={[
          {
            type: "edit",
            onClick: () => {
              const company = viewCompany;
              closeViewModal();
              if (company) handleShow(company);
            },
          },
          {
            type: "delete",
            onClick: () => {
              const company = viewCompany;
              closeViewModal();
              if (company) {
                handleDelete(company.companyId || company.id);
              }
            },
          },
        ]}
      />

    </DashboardShell>
  );
};

export default CompanyManagement;
